import React, { Component } from "react";
import PropTypes from "prop-types";
import i18n from "i18next";
import { LuSparkles, LuLoader } from "react-icons/lu";
import AppModal from "../../common/modal/AppModal";
import { RATIO_PRESETS, getPresetFromValue } from "../../../../core/editor/ratio_presets";
import { convertAspectRatio } from "../../../../common/server_utils.es6";
import { RATIO_REPRODUCE_EVENT } from "./RatioReproduceJobTracker";
import "./_ratioReproduceModal.scss";

// Target-ratio cards. Descriptions live in locales under ratio_reproduce.desc.*
const RATIO_OPTIONS = RATIO_PRESETS.map((p) => ({
  key: p.key,
  label: p.label,
  styleKey: p.styleKey,
  descKey: `ratio_reproduce.desc.${p.key.replace(":", "_")}`,
}));

// Map a known backend error message → localized string. Falls back to a
// status-code bucket, then to the generic "couldn't start" message.
const localizeConvertError = (err) => {
  const serverMsg = err && err.serverMessage;
  const status = err && err.statusCode;
  const KNOWN = {
    "You do not own this training": "ratio_reproduce.errors.not_owner",
  };
  if (serverMsg && KNOWN[serverMsg]) {
    return i18n.t(KNOWN[serverMsg]);
  }
  if (status === 403) {
    return i18n.t("ratio_reproduce.errors.forbidden", {
      defaultValue: "Bu işlem için yetkiniz yok.",
    });
  }
  return i18n.t("ratio_reproduce.error", {
    defaultValue: "İşlem başlatılamadı. Lütfen tekrar deneyin.",
  });
};

const RESOLUTIONS = [
  { key: "1K", labelKey: "ratio_reproduce.res_1k", descKey: "ratio_reproduce.res_1k_desc" },
  {
    key: "2K",
    labelKey: "ratio_reproduce.res_2k",
    descKey: "ratio_reproduce.res_2k_desc",
    default: true,
  },
  { key: "4K", labelKey: "ratio_reproduce.res_4k", descKey: "ratio_reproduce.res_4k_desc" },
];

/**
 * "Farklı Oranda Üret" — reproduce the current training at a different aspect
 * ratio via an async AI job. Picks a target ratio + resolution + new name, then
 * fires POST convert-aspect-ratio. On success it dispatches a
 * `ratioReproduceStarted` window event (picked up by RatioReproduceJobTracker,
 * which polls the job and surfaces a bottom-right toast) and closes — so the
 * multi-minute job runs in the background. The whole picker lives inside a
 * single AppModal (no popup stacking).
 */
export default class RatioReproduceModal extends Component {
  constructor(props) {
    super(props);
    this.state = this.buildInitialState(props);
  }

  buildInitialState(props) {
    const currentRatioKey = getPresetFromValue(props.currentCanvasRatio) || "16:9";
    const firstTarget = RATIO_OPTIONS.find((o) => o.key !== currentRatioKey) || RATIO_OPTIONS[0];
    return {
      currentRatioKey,
      selectedRatioKey: firstTarget.key,
      selectedResolution: (RESOLUTIONS.find((r) => r.default) || RESOLUTIONS[0]).key,
      trainingName: `${props.title || ""} (${firstTarget.key})`.trim(),
      nameDirty: false,
      submitting: false,
    };
  }

  componentDidUpdate(prevProps) {
    if (this.props.visible && !prevProps.visible) {
      this.setState(this.buildInitialState(this.props));
    }
  }

  emitStarted(detail) {
    if (typeof window !== "undefined" && typeof window.CustomEvent === "function") {
      window.dispatchEvent(new CustomEvent(RATIO_REPRODUCE_EVENT, { detail }));
    }
  }

  handleRatioSelect = (key) => {
    this.setState((prev) => ({
      selectedRatioKey: key,
      // Keep the auto-suggested name in sync with the ratio unless the user
      // has typed their own.
      trainingName: prev.nameDirty
        ? prev.trainingName
        : `${this.props.title || ""} (${key})`.trim(),
    }));
  };

  handleClose = () => {
    if (this.state.submitting) return;
    this.props.onToggle();
  };

  handleCreate = async () => {
    const { selectedRatioKey, selectedResolution, trainingName } = this.state;
    const name = trainingName.trim();
    if (this.state.submitting || !name) return;

    this.setState({ submitting: true });
    try {
      const res = await convertAspectRatio({
        trainingId: this.props.trainingId,
        targetAspectRatio: selectedRatioKey,
        name,
        resolution: selectedResolution,
        language: this.props.language,
      });
      const jobId = res && res.jobId;
      if (!jobId) throw new Error("no-job");
      this.emitStarted({ jobId, newTrainingId: res.newTrainingId, name });
    } catch (err) {
      console.error("convertAspectRatio failed", err);
      this.emitStarted({
        failed: true,
        name,
        error: localizeConvertError(err),
      });
    } finally {
      this.setState({ submitting: false });
      this.props.onToggle();
    }
  };

  render() {
    const { currentRatioKey, selectedRatioKey, selectedResolution, trainingName, submitting } =
      this.state;

    const header = i18n.t("ratio_reproduce.question", {
      title: this.props.title || "",
      ratio: currentRatioKey,
      defaultValue: `${this.props.title || ""} (${currentRatioKey}) Bu eğitimi farklı bir ekran oranında yeniden üretmek istiyor musunuz?`,
    });

    return (
      <AppModal
        show={!!this.props.visible}
        onClose={this.handleClose}
        title={i18n.t("ratio_reproduce.title", { defaultValue: "Farklı Oranda Üret" })}
        size="lg"
        showSceneToggle={false}
        className="ratioReproduceModal"
        bodyClassName="ratio-reproduce__body"
        cancelLabel={i18n.t("Cancel") || "İptal"}
        onCancel={this.handleClose}
        cancelDisabled={submitting}
        confirmDisabled={submitting || !trainingName.trim()}
        confirmLabel={
          <>
            {submitting ? (
              <LuLoader aria-hidden="true" className="ratio-reproduce__spin" />
            ) : (
              <LuSparkles aria-hidden="true" />
            )}
            <span>
              {submitting
                ? i18n.t("ratio_reproduce.creating", { defaultValue: "Oluşturuluyor…" })
                : i18n.t("ratio_reproduce.create", { defaultValue: "Oluştur" })}
            </span>
          </>
        }
        onConfirm={this.handleCreate}
      >
        <div className="ratio-reproduce">
          <p className="ratio-reproduce__question">{header}</p>

          <div className="ratio-reproduce__section">
            <label className="ratio-reproduce__section-label">
              {i18n.t("ratio_reproduce.pick_ratio", { defaultValue: "Hedef Oran Seçin" })}
            </label>
            <div className="ratio-reproduce__ratio-grid">
              {RATIO_OPTIONS.map((o) => {
                const selected = o.key === selectedRatioKey;
                return (
                  <button
                    key={o.key}
                    type="button"
                    disabled={submitting}
                    aria-pressed={selected}
                    className={`ratio-reproduce__card ${selected ? "is-selected" : ""}`}
                    onClick={() => this.handleRatioSelect(o.key)}
                  >
                    <span className="ratio-reproduce__card-title">
                      {o.label}{" "}
                      <span className="ratio-reproduce__card-style">({i18n.t(o.styleKey)})</span>
                    </span>
                    <span className="ratio-reproduce__card-desc">{i18n.t(o.descKey)}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="ratio-reproduce__section">
            <label className="ratio-reproduce__section-label">
              {i18n.t("ratio_reproduce.resolution", { defaultValue: "Çözünürlük" })}
            </label>
            <div className="ratio-reproduce__res-grid">
              {RESOLUTIONS.map((r) => {
                const selected = r.key === selectedResolution;
                return (
                  <button
                    key={r.key}
                    type="button"
                    disabled={submitting}
                    aria-pressed={selected}
                    className={`ratio-reproduce__card ratio-reproduce__card--res ${selected ? "is-selected" : ""}`}
                    onClick={() => this.setState({ selectedResolution: r.key })}
                  >
                    <span className="ratio-reproduce__card-title">{i18n.t(r.labelKey)}</span>
                    <span className="ratio-reproduce__card-desc">{i18n.t(r.descKey)}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="ratio-reproduce__section">
            <label htmlFor="ratio-reproduce-name" className="ratio-reproduce__section-label">
              {i18n.t("ratio_reproduce.new_name", { defaultValue: "Yeni Eğitim Adı" })}
            </label>
            <input
              id="ratio-reproduce-name"
              type="text"
              className="ratio-reproduce__input"
              value={trainingName}
              disabled={submitting}
              onChange={(e) => this.setState({ trainingName: e.target.value, nameDirty: true })}
            />
          </div>

          <div className="ratio-reproduce__note">
            {i18n.t("ratio_reproduce.note", {
              defaultValue:
                "Not: Bu işlem yeni bir eğitim oluşturacak ve birkaç dakika sürebilir. Fotoğraflar tamamen aynı kalmayabilir.",
            })}
          </div>
        </div>
      </AppModal>
    );
  }
}

RatioReproduceModal.propTypes = {
  visible: PropTypes.any,
  onToggle: PropTypes.func.isRequired,
  title: PropTypes.string,
  trainingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  currentCanvasRatio: PropTypes.number,
  language: PropTypes.string,
};
